import React from 'react';
import { motion } from 'framer-motion';

export default function SamsungFoldNew() {
  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  const slideLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0 },
  };

  const hoverImage = 'transition-all duration-700 hover:scale-110 hover:rotate-1';
  const cardHover = 'hover:shadow-2xl hover:shadow-purple-500/20 hover:-translate-y-2';

  return (
    <div className=" mt-10 min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 py-10 px-4 md:px-6 lg:px-8 xl:px-12">
      {/* Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative max-w-8xl mx-auto grid grid-cols-1 xl:grid-cols-3 gap-8 lg:gap-12">

        {/* Main Content */}
        <div className="xl:col-span-2 space-y-8 lg:space-y-12">

          {/* Hero Section Card */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            variants={slideUp}
            className={`bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl lg:rounded-[2rem] shadow-2xl overflow-hidden group border border-white/20 ${cardHover} transition-all duration-500`}
          >
            <div className="relative h-72 sm:h-80 md:h-96 lg:h-[450px] overflow-hidden">
              <img
                src="https://eskimi-creatives.com/Samsung_Fold_6/site-images/1.jpg"
                alt="Samsung Galaxy Fold Campaign"
                className={`w-full h-full object-cover ${hoverImage}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold px-4 py-2 rounded-full backdrop-blur-sm border border-white/20">
                Premium Campaign
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-white text-sm font-medium">Live Campaign</span>
                  </div>
                  <div className="flex items-center space-x-2 text-white/80 text-sm">
                    <span>👁️ 2.4M</span>
                    <span>❤️ 158K</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 lg:p-8">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent mb-3 lg:mb-4">
                Samsung Galaxy Fold 6
              </h1>
              <p className="text-gray-300 text-sm lg:text-base mb-4 lg:mb-6 leading-relaxed max-w-2xl">
                Experience the future of foldable technology through this engaging advertisement campaign. Where innovation meets premium design in perfect harmony.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-white/10">
                <div className="flex items-center space-x-4 text-xs text-gray-400">
                  <span className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
                    <span>Premium Advertising</span>
                  </span>
                  <span>💬 36 Comments</span>
                </div>
                <button className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-xs font-semibold hover:from-purple-500 hover:to-pink-500 transition-all duration-300 transform hover:scale-105">
                  View Details
                </button>
              </div>
            </div>
          </motion.div>

          {/* Main Video Block */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            variants={slideUp}
            className={`bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl shadow-2xl p-4 lg:p-6 border border-white/20 ${cardHover} transition-all duration-500`}
          >
            <div className="relative overflow-hidden rounded-2xl group">
              <video
                src="https://demo.eskimi-creatives.com/samsung-i3/300x600_4/images/video.mp4"
                className="w-full max-w-sm mx-auto rounded-2xl shadow-lg transition-transform duration-500 group-hover:scale-105"
                autoPlay
                muted
                playsInline
                loop
              ></video>
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl flex items-center justify-center">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <div className="w-0 h-0 border-l-[12px] border-l-white border-y-[8px] border-y-transparent ml-1"></div>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-2">
              <div className="flex items-center space-x-4 text-gray-400 text-sm">
                <span className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span>Live Video</span>
                </span>
                <span>📐 1920×1080</span>
              </div>
              <span className="text-purple-300 font-mono text-sm">ID: 1557042</span>
            </div>
          </motion.div>

          {/* Elegant Divider */}
          <div className="relative flex items-center justify-center my-8 lg:my-12">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gradient-to-r from-transparent via-white/20 to-transparent"></div>
            </div>
            <div className="relative flex justify-center">
              <div className="px-6 py-2 bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-full border border-white/20">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse delay-200"></div>
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse delay-400"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Second Feature Card */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            variants={slideUp}
            className={`bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden group border border-white/20 ${cardHover} transition-all duration-500`}
          >
            <div className="relative h-72 sm:h-80 md:h-96 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1480714378408-67cf0d13bc1f?auto=format&fit=crop&w=2070&q=80"
                alt="Engaging Visuals"
                className={`w-full h-full object-cover ${hoverImage}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-semibold px-4 py-2 rounded-full backdrop-blur-sm border border-white/20">
                Visual Experience
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                    <span className="text-white text-sm font-medium">Featured Content</span>
                  </div>
                  <div className="flex items-center space-x-2 text-white/80 text-sm">
                    <span>👁️ 1.8M</span>
                    <span>❤️ 92K</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 lg:p-10">
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent mb-4">
                Immersive Visuals
              </h2>
              <p className="text-gray-300 text-base lg:text-lg mb-6 leading-relaxed">
                Capturing the essence of creativity through stunning visuals and thoughtful design that brings your content to life.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-white/10">
                <div className="flex items-center space-x-6 text-sm text-gray-400">
                  <span className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                    <span>Creative Showcase</span>
                  </span>
                  <span>💬 24 Comments</span>
                </div>
                <button className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-semibold hover:from-blue-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105">
                  Explore More
                </button>
              </div>
            </div>
          </motion.div>

          {/* Secondary Video Block */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            variants={slideUp}
            className={`bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl shadow-2xl p-4 lg:p-6 border border-white/20 ${cardHover} transition-all duration-500`}
          >
            <div className="relative overflow-hidden rounded-2xl group">
              <video
                src="https://eskimi-creatives.com/host/Johnnie_Walker/JW_FY25_video/video_320480.mp4"
                className="w-full max-w-lg mx-auto rounded-2xl shadow-lg transition-transform duration-500 group-hover:scale-105"
                autoPlay
                muted
                playsInline
                loop
              ></video>
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl flex items-center justify-center">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <div className="w-0 h-0 border-l-[12px] border-l-white border-y-[8px] border-y-transparent ml-1"></div>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-2">
              <div className="flex items-center space-x-4 text-gray-400 text-sm">
                <span className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  <span>Mobile Format</span>
                </span>
                <span>📐 320×480</span>
              </div>
              <span className="text-orange-300 font-mono text-sm">ID: 1557043</span>
            </div>
          </motion.div>

          {/* Final Divider */}
          <div className="relative flex items-center justify-center my-8 lg:my-12">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gradient-to-r from-transparent via-white/20 to-transparent"></div>
            </div>
            <div className="relative flex justify-center">
              <div className="px-6 py-2 bg-gradient-to-r from-green-600/20 to-blue-600/20 backdrop-blur-sm rounded-full border border-white/20">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse delay-200"></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-400"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Third Creative Card */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            variants={slideUp}
            className={`bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden group border border-white/20 ${cardHover} transition-all duration-500`}
          >
            <div className="relative h-72 sm:h-80 md:h-96 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1480714378408-67cf0d13bc1f?auto=format&fit=crop&w=2070&q=80"
                alt="Creative Showcase"
                className={`w-full h-full object-cover ${hoverImage}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute top-4 right-4 bg-gradient-to-r from-green-600 to-blue-600 text-white text-xs font-semibold px-4 py-2 rounded-full backdrop-blur-sm border border-white/20">
                Design Excellence
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-white text-sm font-medium">Premium Design</span>
                  </div>
                  <div className="flex items-center space-x-2 text-white/80 text-sm">
                    <span>👁️ 3.1M</span>
                    <span>❤️ 203K</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 lg:p-10">
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-white via-green-200 to-blue-200 bg-clip-text text-transparent mb-4">
                Creative Excellence
              </h2>
              <p className="text-gray-300 text-base lg:text-lg mb-6 leading-relaxed">
                A brilliant example of modern advertisement design paired with clean aesthetics and innovative storytelling approaches.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-white/10">
                <div className="flex items-center space-x-6 text-sm text-gray-400">
                  <span className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-full"></div>
                    <span>Award Winning</span>
                  </span>
                  <span>💬 52 Comments</span>
                </div>
                <button className="px-6 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-full text-sm font-semibold hover:from-green-500 hover:to-blue-500 transition-all duration-300 transform hover:scale-105">
                  Learn More
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Enhanced Sidebar */}
        <div className="space-y-8 lg:space-y-10">

          {/* Sidebar Featured Video */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            variants={slideLeft}
            className="relative overflow-hidden rounded-3xl group"
          >
            <div className="relative">
              <video
                className="w-full h-[500px] lg:h-[700px] object-cover rounded-3xl shadow-2xl transition-transform duration-500 group-hover:scale-105"
                autoPlay
                muted
                playsInline
                loop
              >
                <source src="https://demo.eskimi-creatives.com/samsung-i3/300x600_4/images/video.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-3xl"></div>
              
              {/* Video Overlay */}
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl flex items-center justify-center">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <div className="w-0 h-0 border-l-[16px] border-l-white border-y-[12px] border-y-transparent ml-1"></div>
                </div>
              </div>

              {/* Sidebar Video Info */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-black/50 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-bold text-lg">Featured Showcase</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-white text-sm">Live</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-300">📐 300×700</span>
                    <span className="text-purple-300 font-mono">ID: 1557042</span>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                    <span>👁️ 2.8M views</span>
                    <span>⏱️ 2:34</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Sidebar Card */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            variants={slideLeft}
            className={`bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden group border border-white/20 ${cardHover} transition-all duration-500`}
          >
            <div className="relative h-48 lg:h-56 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=2070&q=80"
                alt="Sidebar Premium Content"
                className={`w-full h-full object-cover ${hoverImage}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute top-4 right-4 bg-gradient-to-r from-pink-600 to-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm border border-white/20">
                Premium
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
                    <span className="text-white text-sm font-medium">Trending</span>
                  </div>
                  <div className="text-white/80 text-sm">⭐ 4.9</div>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-bold bg-gradient-to-r from-white to-pink-200 bg-clip-text text-transparent">
                  Premium Showcase
                </h3>
                <span className="text-pink-300 font-mono text-sm">ID: 1557042</span>
              </div>
              <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                Highlighting key advertising assets with smooth animations, premium design elements, and engaging visual storytelling.
              </p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-white/10">
                  <span className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"></div>
                    <span>Featured Content</span>
                  </span>
                  <span>💬 76 Comments</span>
                </div>
                <button className="w-full px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-xl text-sm font-semibold hover:from-pink-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105">
                  View Collection
                </button>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
}

// import React from 'react';
// import { motion } from 'framer-motion';

// export default function SamsungFoldNew() {
//   const slideUp = {
//     hidden: { opacity: 0, y: 50 },
//     visible: { opacity: 1, y: 0 },
//   };

//   const hoverImage = 'transition-transform duration-500 hover:scale-105';

//   return (
//     <div className="min-h-screen bg-gray-50 py-10 px-4 md:px-10 lg:px-20 mt-10">
//       <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10">

//         {/* Main Content */}
//         <div className="lg:col-span-2 space-y-12">

//           {/* Section Card */}
//           <motion.div
//             initial="hidden"
//             whileInView="visible"
//             viewport={{ once: true }}
//             transition={{ duration: 0.6 }}
//             variants={slideUp}
//             className="bg-white rounded-3xl shadow-lg overflow-hidden group"
//           >
//             <div className="relative h-64 md:h-[400px] overflow-hidden">
//               <img
//                 src="https://eskimi-creatives.com/Samsung_Fold_6/site-images/1.jpg"
//                 alt="Ad Visual"
//                 className={`w-full h-full object-cover ${hoverImage}`}
//               />
//               <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white text-xs px-3 py-1 rounded-full">
//                 Advertising
//               </div>
//             </div>
//             <div className="p-8">
//               <h1 className="text-4xl font-bold text-gray-800 mb-4">Stunning Campaign</h1>
//               <p className="text-gray-600 mb-6 leading-relaxed">
//                 Explore the future of foldable phones through this engaging advertisement campaign.
//               </p>
//               <div className="flex justify-between text-xs text-gray-500 pt-4 border-t border-gray-200">
//                 <span>Category: Advertising</span>
//                 <span>36 Comments</span>
//               </div>
//             </div>
//           </motion.div>

//           {/* Video Block */}
//           <motion.div
//             initial="hidden"
//             whileInView="visible"
//             viewport={{ once: true }}
//             transition={{ duration: 0.6 }}
//             variants={slideUp}
//             className="bg-white rounded-3xl shadow-lg p-4 flex flex-col items-center"
//           >
//             <video
//               src="https://demo.eskimi-creatives.com/samsung-i3/300x600_4/images/video.mp4"
//               className="w-full max-w-[500px] rounded-lg"
//               autoPlay
//               muted
//               playsInline
//               loop
//             ></video>
//             <div className="flex justify-between text-xs text-gray-500 mt-2 w-full max-w-[500px]">
//               <span>300×250</span>
//               <span>ID: 1557042</span>
//             </div>
//           </motion.div>

//           {/* Divider */}
//           <div className="border-t-2 border-dashed border-gray-300 my-8"></div>

//           {/* Second Image Block */}
//           <motion.div
//             initial="hidden"
//             whileInView="visible"
//             viewport={{ once: true }}
//             transition={{ duration: 0.6 }}
//             variants={slideUp}
//             className="bg-white rounded-3xl shadow-lg overflow-hidden group"
//           >
//             <div className="relative h-64 md:h-[400px] overflow-hidden">
//               <img
//                 src="https://images.unsplash.com/photo-1480714378408-67cf0d13bc1f?auto=format&fit=crop&w=2070&q=80"
//                 alt="Ad Visual"
//                 className={`w-full h-full object-cover ${hoverImage}`}
//               />
//               <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white text-xs px-3 py-1 rounded-full">
//                 Advertising
//               </div>
//             </div>
//             <div className="p-8">
//               <h1 className="text-4xl font-bold text-gray-800 mb-4">Engaging Visuals</h1>
//               <p className="text-gray-600 mb-6 leading-relaxed">
//                 Capturing the essence of creativity through stunning visuals and thoughtful design.
//               </p>
//               <div className="flex justify-between text-xs text-gray-500 pt-4 border-t border-gray-200">
//                 <span>Category: Advertising</span>
//                 <span>36 Comments</span>
//               </div>
//             </div>
//           </motion.div>

//           {/* Video Block */}
//           <motion.div
//             initial="hidden"
//             whileInView="visible"
//             viewport={{ once: true }}
//             transition={{ duration: 0.6 }}
//             variants={slideUp}
//             className="bg-white rounded-3xl shadow-lg p-4 flex flex-col items-center"
//           >
//             <video
//               src="https://eskimi-creatives.com/host/Johnnie_Walker/JW_FY25_video/video_320480.mp4"
//               className="w-full max-w-[500px] rounded-lg"
//               autoPlay
//               muted
//               playsInline
//               loop
//             ></video>
//             <div className="flex justify-between text-xs text-gray-500 mt-2 w-full max-w-[500px]">
//               <span>320×480</span>
//               <span>ID: 1557042</span>
//             </div>
//           </motion.div>

//           {/* Divider */}
//           <div className="border-t-2 border-dashed border-gray-300 my-8"></div>

//           {/* Third Image Block */}
//           <motion.div
//             initial="hidden"
//             whileInView="visible"
//             viewport={{ once: true }}
//             transition={{ duration: 0.6 }}
//             variants={slideUp}
//             className="bg-white rounded-3xl shadow-lg overflow-hidden group"
//           >
//             <div className="relative h-64 md:h-[400px] overflow-hidden">
//               <img
//                 src="https://images.unsplash.com/photo-1480714378408-67cf0d13bc1f?auto=format&fit=crop&w=2070&q=80"
//                 alt="Ad Visual"
//                 className={`w-full h-full object-cover ${hoverImage}`}
//               />
//               <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white text-xs px-3 py-1 rounded-full">
//                 Advertising
//               </div>
//             </div>
//             <div className="p-8">
//               <h1 className="text-4xl font-bold text-gray-800 mb-4">Creative Showcase</h1>
//               <p className="text-gray-600 mb-6 leading-relaxed">
//                 A brilliant example of modern advertisement design paired with clean aesthetics.
//               </p>
//               <div className="flex justify-between text-xs text-gray-500 pt-4 border-t border-gray-200">
//                 <span>Category: Advertising</span>
//                 <span>36 Comments</span>
//               </div>
//             </div>
//           </motion.div>
//         </div>

//         {/* Sidebar */}
//         <div className="space-y-10">

//           {/* Sidebar Video */}
//           <motion.div
//             initial="hidden"
//             whileInView="visible"
//             viewport={{ once: true }}
//             transition={{ duration: 0.6 }}
//             variants={slideUp}
//             className=" overflow-hidden relative"
//           >
//             <video
//               className="w-full h-[700px] object-cover"
//               autoPlay
//               muted
//               playsInline
//               loop
//             >
//               <source src="https://demo.eskimi-creatives.com/samsung-i3/300x600_4/images/video.mp4" type="video/mp4" />
//               Your browser does not support the video tag.
//             </video>
//             <div className="absolute bottom-4 left-4 right-4 flex justify-between text-xs text-white bg-black bg-opacity-50 px-3 py-1 rounded">
//               <span>300×700</span>
//               <span>ID: 1557042</span>
//             </div>
//           </motion.div>

//           {/* Sidebar Image Card */}
//           <motion.div
//             initial="hidden"
//             whileInView="visible"
//             viewport={{ once: true }}
//             transition={{ duration: 0.6 }}
//             variants={slideUp}
//             className="bg-white rounded-3xl shadow-lg overflow-hidden group"
//           >
//             <div className="relative h-48 overflow-hidden">
//               <img
//                 src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=2070&q=80"
//                 alt="Sidebar Visual"
//                 className={`w-full h-full object-cover ${hoverImage}`}
//               />
//               <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white text-xs px-3 py-1 rounded-full">
//                 Sidebar
//               </div>
//             </div>
//             <div className="p-6">
//               <h2 className="text-xl font-bold text-gray-800 mb-2">ID: 1557042</h2>
//               <p className="text-gray-600 text-sm mb-4 leading-relaxed">
//                 Highlighting key ad assets with smooth animations and clean visuals.
//               </p>
//               <div className="flex justify-between text-xs text-gray-500 pt-4 border-t border-gray-200">
//                 <span>Category: Advertising</span>
//                 <span>76 Comments</span>
//               </div>
//             </div>
//           </motion.div>

//         </div>
//       </div>
//     </div>
//   );
// }